/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from "react";
import {
  IonContent,
  IonButton,
  IonList,
  IonModal,
  IonIcon,
  IonToast,
  IonSpinner,
  IonButtons,
  IonHeader,
} from "@ionic/react";
import { closeCircleOutline } from "ionicons/icons";
import { sendInvite } from "../../../services/sendrequest";
import { countryCodes } from "../../../utils/constants";
import useAuth from "../../../hooks/useAuth";
import SimpleInput from "../../../components/ui/SimpleInput";
import OrgShareCard from "./OrgShareCard";
import { SHARE_DIRECTORY_INTRO } from "../../../services/orgRelationship";

// Helper to find country code match using the same logic as registration
const findCountryCodeMatch = (input: string) => {
  // Sort country codes by length (longest first) to match correctly
  const sortedCodes = countryCodes
    .map((c) => c.code)
    .sort((a, b) => b.length - a.length);

  for (const code of sortedCodes) {
    if (input.startsWith(code)) {
      const match = {
        countryCode: code,
        localNumber: input.slice(code.length),
      };
      return match;
    }
  }

  return null;
};

// Helper to check if the country code is valid
const isValidCountryCode = (num: string) => {
  if (!num.startsWith("+")) return false;
  const match = findCountryCodeMatch(num);
  return match !== null;
};

// Helper to check if the input is an email (basic structure)
const isEmailInput = (input: string) => {
  // Basic RFC 5322 email regex
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input.trim());
};

// Helper to determine if input should be treated as phone (only numbers and +)
const isPhoneInput = (input: string) => {
  // Only digits and + allowed for phone
  return /^[\d+\-\s()]*$/.test(input.trim());
};

// Helper to validate phone number
const isValidPhone = (num: string) => {
  const trimmed = num.trim();
  if (!trimmed.startsWith("+")) return false;

  const match = findCountryCodeMatch(trimmed);
  if (!match) return false;

  // Check total length and local number format
  const localNumber = match.localNumber;
  return /^\d{6,15}$/.test(localNumber);
};

type DataSharingModalProps = {
  isOpen: boolean;
  mode?: "share" | "follow" | null;
  onClose: () => void;
  // Which flow the user came in on. "people" = the original invite-a-person form; "org" = an
  // organization-focused view (search + org list, no people-invite clutter). ACDPA-186.
  focus?: "people" | "org";
  /**
   * Called once an invite to a person (caregiver, family member, or another patient) sends
   * successfully (F-515) — so the host can show a confirmation toast that survives this popup
   * closing, the same pattern OrgShareCard's `onShared` uses for F-470: a toast that is local to
   * this modal would unmount along with it before ever becoming visible, so the host (which stays
   * mounted) owns the confirmation. Not called on failure — the popup stays open with the error
   * shown locally instead.
   */
  onSuccess?: () => void;
};

const DataSharingModal: React.FC<DataSharingModalProps> = ({
  mode = "share",
  isOpen: propIsOpen = false,
  onClose,
  focus = "people",
  onSuccess,
}) => {
  const [isOpen, setIsOpen] = useState(propIsOpen);
  // The org-only popup's single search field (the regular invite reuses its email/phone input
  // instead, so each popup has exactly one search).
  const [orgSearch, setOrgSearch] = useState("");
  const [codeModal, setCodeModal] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([""]);
  const [countryCode, setCountryCode] = useState<{ [key: number]: string }>({
    0: "+1",
  });
  const [countryCodeErrors, setCountryCodeErrors] = useState<boolean[]>([
    false,
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    color: "danger" | "success";
  }>({ show: false, message: "", color: "danger" });

  const prevStep = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const onCancel = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
    setPhoneNumbers([""]);
    setCountryCodeErrors([false]);
  };

  // Update error state for country code validity and auto-add +
  const handlePhoneNumberChange = (idx: number, val: string) => {
    const updatedVal = val;

    // If input is only numbers (phone mode), auto-add + if needed
    if (isPhoneInput(updatedVal) && updatedVal && !updatedVal.startsWith("+")) {
      // Check if it matches any country code
      // const codes = countryCodes.map((country) =>
      //   country.code.replace("+", "")
      // );
      // const matchingCode = codes.find((code) => updatedVal.startsWith(code));
    }

    const updated = [...phoneNumbers];
    updated[idx] = updatedVal;
    setPhoneNumbers(updated);
  };

  const allValid =
    phoneNumbers.length > 0 &&
    phoneNumbers.every((num, idx) => {
      const trimmed = num.trim();
      if (!trimmed) return false; // Empty strings are not valid

      // If input contains non-numeric characters (except +), treat as email
      if (!isPhoneInput(trimmed)) {
        // Treat as email - validate email format
        return isEmailInput(trimmed);
      } else {
        if (/^[\d()+\- ]+$/.test(trimmed)) {
          const match = findCountryCodeMatch(countryCode[idx]);
          if (!match) return false; // No valid country code found

          // Check if local number has at least 6 digits
          const localNumber = trimmed.replace(/[^\d+]/g, "");
          return /^\d{7,15}$/.test(localNumber);
        }
        return false;
      }
    });

  const handleSendInvite = async () => {
    if (allValid && !isLoading) {
      setIsLoading(true);

      // Separate email and phone data for correct typing
      const emailData = phoneNumbers
        .filter((num) => {
          const trimmed = num.trim();
          return !isPhoneInput(trimmed) && isEmailInput(trimmed);
        })
        .map((num) => ({
          email: num.trim(),
          name: "",
          message: "You're invited you to join Arc Connect network!",
          invitation_type: "email",
          invitation_method: mode ?? "follow",
          request_type: mode === "follow" ? "follow" : "follower",
          expiration_days: 7,
        }));

      const phoneData = phoneNumbers
        .map((num, idx) => ({ num, idx }))
        .filter(({ num }) => {
          const trimmed = num.trim();
          return isPhoneInput(trimmed);
        })
        .map(({ num, idx }) => {
          const match = findCountryCodeMatch(countryCode[idx] || "");
          return {
            phone: (countryCode[idx] || "") + num.replace(/[^\d]/g, ""),
            phone_country_code: match
              ? match.countryCode
              : countryCode[idx] || "",
            name: "",
            message: "",
            invitation_type: "phone",
            invitation_method: mode ?? "follow",
            expiration_days: 14,
          };
        });

      try {
        if (emailData.length > 0 || phoneData.length > 0) {
          await sendInvite([...emailData, ...phoneData]);
        }
        if (emailData.length > 0 || phoneData.length > 0) {
          // F-515: close on success, same as F-470 (OrgShareCard's `onShared`). The confirmation
          // toast is shown by the host page (see MyFollowers.tsx / MyFollowing.tsx), not by this
          // component's own IonToast below — that one unmounts along with the modal before it
          // could ever be seen, since prevStep() closes this popup right away.
          onSuccess?.();
          prevStep();
        }
      } catch (error) {
        // On failure the popup stays open (no prevStep()) and shows the error locally.
        setToast({
          show: true,
          message: "Failed to send invite.",
          color: "danger",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleRemoveInput = (idx: number) => {
    if (phoneNumbers.length > 1) {
      setPhoneNumbers(phoneNumbers.filter((_, i) => i !== idx));
      setCountryCodeErrors(countryCodeErrors.filter((_, i) => i !== idx));
    }
  };

  const handleClearOrRemove = (idx: number) => {
    const currentValue = phoneNumbers[idx];
    if (idx === 0) {
      // First input box: only clear the value
      const updated = [...phoneNumbers];
      updated[idx] = "";
      setPhoneNumbers(updated);
      setCountryCodeErrors(
        updated.map((num) => (num ? !isValidCountryCode(num) : false)),
      );
    } else {
      // Additional input boxes: clear first, remove on second click
      if (currentValue !== "") {
        const updated = [...phoneNumbers];
        updated[idx] = "";
        setPhoneNumbers(updated);
        setCountryCodeErrors(
          updated.map((num) => (num ? !isValidCountryCode(num) : false)),
        );
      } else {
        handleRemoveInput(idx);
      }
    }
  };

  const closeButton = (
    <button
      className="flex items-center bg-none border-none p-0 cursor-pointer"
      onClick={prevStep}
      type="button"
      disabled={isLoading}
      style={{
        minWidth: 90,
        height: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "18px",
        // fontWeight: 500,
        color: isLoading ? "#ccc" : "#007AFF",
        borderRadius: 8,
        background: "none",
        opacity: isLoading ? 0.5 : 1,
      }}
    >
      <IonIcon icon={closeCircleOutline} className=" text-blue-500" />
      <span className="ml-1 text-blue-500 font-medium ">Close</span>
    </button>
  );

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => {
        setIsOpen(false);
        onClose();
      }}
      style={{ borderRadius: "25px", overflow: "hidden" }}
      {...(window.innerWidth >= 768
        ? {}
        : {
            breakpoints: [0, 0.5, 0.95, 1],
            initialBreakpoint: 0.95,
            handle: true,
            // Keep the header (Close, title, search) in place and scroll only the results at
            // every sheet height, instead of dragging the sheet up first (F-468).
            expandToScroll: false,
          })}
    >
      {focus === "org" ? (
        <>
          {/* F-468: Close, title, sub-header and search sit in a FIXED header so the popup can
              always be closed while the results scroll; top padding trimmed so no space is
              wasted. Only IonContent below scrolls. */}
          <IonHeader className="ion-no-border" style={{ background: "white" }}>
            <div style={{ background: "white", padding: "4px 16px 0", textAlign: "center" }}>
              <div className="flex justify-between items-center">{closeButton}</div>
              <h1
                style={{
                  fontFamily: "Figtree",
                  fontWeight: 700,
                  fontSize: "20px",
                  lineHeight: "28px",
                  color: "#000000",
                  margin: "0",
                }}
              >
                Share with an organization
              </h1>
              <p
                style={{
                  fontFamily: "Figtree",
                  fontWeight: 400,
                  fontSize: "15px",
                  lineHeight: "21px",
                  color: "#586670",
                  margin: "4px auto 12px",
                  maxWidth: 360,
                }}
              >
                {SHARE_DIRECTORY_INTRO}
              </p>
              {/* The single search for this popup — native input styled like SimpleInput.
                  Organizations are listed only once the patient types here. */}
              <input
                className="org-search-input"
                type="text"
                aria-label="Search organizations"
                placeholder="Search organizations"
                value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                style={{ marginBottom: 8 }}
              />
            </div>
          </IonHeader>
          <IonContent style={{ "--background": "white" }}>
            <div className="mx-auto w-full" style={{ padding: "0 16px", textAlign: "left" }}>
              {/* F-470: a successful share closes this whole sheet. F-524: pressing Share sends
                  the request directly — there is no confirm popup on top of this sheet any more. */}
              <OrgShareCard search={orgSearch} hideWhenBlocked onShared={prevStep} />
            </div>
          </IonContent>
        </>
      ) : (
      <IonContent style={{ "--background": "white", textAlign: "center" }}>
        <div className="flex justify-between items-center px-4 pt-8">
          {closeButton}
          {focus === "people" && (
            <IonButtons slot="end">
              <IonButton
                onClick={handleSendInvite}
                disabled={!allValid || isLoading}
                className={`post-button ${
                  allValid ? "post-button-enabled" : "post-button-disabled"
                }`}
              >
                {isLoading ? (
                  <div
                    style={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <IonSpinner
                      name="crescent"
                      style={{ width: "16px", height: "16px" }}
                    />
                    <span>Sending...</span>
                  </div>
                ) : mode === "share" ? (
                  "Send Invite"
                ) : (
                  "Send Request"
                )}
              </IonButton>
            </IonButtons>
          )}
        </div>

        <>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: "32px",
            marginBottom: "16px",
            position: "relative",
          }}
        >
          <h1
            style={{
              position: "absolute",
              left: "50%",
              top: "0px",
              transform: "translateX(-50%)",
              fontFamily: "Figtree",
              fontStyle: "normal",
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "28px",
              textAlign: "center",
              letterSpacing: "0.374px",
              color: "#000000",
              width: "322px",
              maxWidth: "90vw",
            }}
          >
            {mode === "share" ? "Share my data" : "Follow someone else"}
          </h1>
          <p
            style={{
              fontFamily: "Figtree",
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: "22px",
              textAlign: "center",
              color: "#333",
              margin: "48px 0 0 0",
              width: "100%",
              maxWidth: 350,
            }}
          >
            {mode === "share" ? (
              <>
                Invite someone to monitor your
                <br />
                treatments by entering their phone number or email below.
              </>
            ) : (
              "Send a request to another person to view their therapy data."
            )}
          </p>
        </div>

        <div
          className="max-h-[60vh] overflow-y-auto mx-auto w-full px-4"
          style={{
            padding: "0 40px",
            textAlign: "left",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              fontFamily: "Figtree",
              fontWeight: 600,
              fontSize: "15px",
              lineHeight: "20px",
              color: "#586670",
              zIndex: 2,
              overflowY: "auto",
              marginBottom: "16px",
            }}
          >
            {mode === "share" ? "Invite Viewers" : "Send Request"}
          </div>

          <IonList>
            {phoneNumbers.map((num, idx) => (
              <div
                key={`row-${idx}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                  width: "100%",

                  minHeight: "48px",
                }}
              >
                <div style={{ width: "100%" }}>
                  <SimpleInput
                    key={`input-${idx}`}
                    value={num}
                    countryCode={countryCode[idx] || ""}
                    onCountryCodeChange={(val) =>
                      setCountryCode((prev) => ({ ...prev, [idx]: val }))
                    }
                    setEmailMode={(val: boolean) => {
                      if (val) {
                        // Clear all phone numbers when switching to email mode
                        setPhoneNumbers([""]);
                        setCodeModal(false);
                        setCountryCodeErrors([false]);
                      } else {
                        setCodeModal(true);
                      }
                    }}
                    onChange={(val) => handlePhoneNumberChange(idx, val)}
                    isValid={(() => {
                      const trimmed = num.trim();
                      if (!trimmed) return true; // Empty is valid (not invalid)

                      if (!isPhoneInput(trimmed)) {
                        // Treat as email
                        return isEmailInput(trimmed);
                      } else {
                        // Treat as phone
                        // Check if it can be converted to numbers
                        const numeric = trimmed.replace(/\D/g, "");
                        if (/^[\d()+-]+$/.test(trimmed)) {
                          const match = findCountryCodeMatch(trimmed);
                          if (!match) return false;
                          return /^\d{6,15}$/.test(match.localNumber);
                        }
                        return false;
                      }
                    })()}
                    invalidCountryCode={(() => {
                      const trimmed = num.trim();
                      if (!isPhoneInput(trimmed)) return false; // Email mode
                      // Check if it can be converted to numbers
                      const numeric = trimmed.replace(/\D/g, "");
                      return numeric.length > 0
                        ? countryCodeErrors[idx]
                        : false;
                    })()}
                    onClearOrRemove={() => handleClearOrRemove(idx)}
                    isFirstInput={idx === 0}
                    showClearButton={idx === 0 ? num !== "" : true}
                    placeholder={
                      idx === 0 && mode === "share"
                        ? "Email, phone, or organization"
                        : "Email or Phone Number"
                    }
                  />
                </div>
              </div>
            ))}
          </IonList>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "30px",
            }}
          >
            <button
              className="add-device-button w-[200px] flex"
              onClick={() => {
                setPhoneNumbers((prev) => [...prev, ""]);
                setCountryCodeErrors((prev) => [...prev, false]);
              }}
              disabled={phoneNumbers.length >= 20}
              style={{
                opacity: phoneNumbers.length >= 10 ? 0.5 : 1,
                pointerEvents: phoneNumbers.length >= 20 ? "none" : "auto",
              }}
            >
              + Add another
            </button>
          </div>

          {/* Org autocomplete — the email/phone input above is the single search; while typing,
              matches show as a compact dropdown (header hidden) so it reads as part of the invite
              form rather than a separate card. F-532 (UAT, URS_6.8): the Individual pop-up
              (this branch) no longer shows the "Share with a care organization" / "Start typing a
              name above..." hint — that belongs to the Organization path, which never rendered
              this title/subtitle to begin with (see the focus === "org" branch above). */}
          {mode === "share" && (
            <OrgShareCard
              autocomplete
              search={phoneNumbers[0] || ""}
              hideWhenBlocked
            />
          )}
        </div>
        </>

        {/* <div
          style={{
            position: "absolute",
            bottom: "70px",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IonButton
            expand="block"
            onClick={handleSendInvite}
            disabled={!allValid || isLoading}
            className="primary-button"
            style={{
              "--background": allValid && !isLoading ? "#00BCD4" : "#DADCDF",
              "--color": "white",
              "--border-radius": "30px",
              "--box-shadow": "none",
              height: "48px",
              display: "flex",
              alignItems: "center",
              fontSize: "20px",
              fontWeight: "600",
              fontStyle: "semi-bold",
              fontFamily: "Figtree",
              justifyContent: "center",
              width: "320px",
              maxWidth: "90vw",
            }}
          >
            {isLoading ? (
              <div
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
          <IonSpinner
            name="crescent"
            style={{ width: "16px", height: "16px" }}
          />
          <span>Sending...</span>
              </div>
            ) : mode === "share" ? (
              "Send Invite"
            ) : (
              "Send Request"
            )}
          </IonButton>
        </div> */}

        <IonToast
          isOpen={toast.show}
          message={toast.message}
          duration={2000}
          position="bottom"
          color={toast.color}
          onDidDismiss={() => setToast({ ...toast, show: false })}
        />
      </IonContent>
      )}
    </IonModal>
  );
};

export default DataSharingModal;
